import { touchWebClient, webApiClient } from 'tradera/utils/http';
import featureToggles from 'tradera/apps/my-tradera/app/utils/feature-toggles';
import { isServer } from 'tradera/utils/nextjs';
import type {
    SaveDraftApiResponse,
    ItemDraft,
    CreateDraftFromTemplateApiResponse,
    CreateDraftFromTemplateApiRequest,
    GetDraftApiResponse,
    SaveDraftApiRequest,
    ItemDraftResponse
} from 'tradera/state/services/types/webapi-selling-generated';
import type { SyiState } from 'tradera/apps/syi/state/reducer';
import { isNullOrUndefined } from 'tradera/utils/condition-check';
import qs from 'qs';

const defaultDrafts = isServer ? {} : window.sellingGlobalData?.defaultDrafts;

const resetUnwantedProperties = (draft: ItemDraft | null | undefined) => {
    return { ...draft, itemId: 0 };
};

const getType = (draft: ItemDraft) => {
    const hasOpeningPrice = !isNullOrUndefined(draft.openingBid);
    const hasFixedPrice = !isNullOrUndefined(draft.fixedPrice);
    if (hasOpeningPrice && !hasFixedPrice) {
        return 'Auction';
    }

    if (hasOpeningPrice && hasFixedPrice) {
        return 'AuctionBin';
    }

    if (!hasOpeningPrice && hasFixedPrice) {
        return 'PureBin';
    }

    return 'Unknown';
};

const ensureApplicationId = (draft: ItemDraft) => {
    return {
        ...draft,
        metadata: { ...draft.metadata, applicationId: 'SYI-Riot' }
    };
};

const isPureBin = (draft: ItemDraft) => draft && getType(draft) === 'PureBin';

const isAuctionBin = (draft: ItemDraft) =>
    draft && getType(draft) === 'AuctionBin';

const getDefaultTemplateValue = <K extends keyof ItemDraft>(propertyName: K) =>
    defaultDrafts?.newTemplate?.[propertyName];

export const loadDraft = async (
    draftId: string,
    languageCodeIso2: string,
    countryCodeIso2: string
) => {
    if (!draftId) {
        throw 'missing draftId';
    }

    const response = await webApiClient.get<GetDraftApiResponse>(
        `/selling/web/drafts/${draftId}?${qs.stringify({
            languageCodeIso2,
            countryCodeIso2
        })}`
    );

    return response.data.draft;
};

const loadDefaultDraft = () => ({
    ...defaultDrafts.newItem,
    allowedBuyerRegion: undefined
});

const loadDefaultTemplateDraft = () => ({
    ...defaultDrafts.newTemplate,
    allowedBuyerRegion: undefined
});

const loadDraftFromTemplate = async (templateId: string) => {
    try {
        const response = await touchWebClient.post<
            CreateDraftFromTemplateApiResponse,
            CreateDraftFromTemplateApiRequest
        >('/selling/api/drafts/from-template', {
            templateId
        });
        const draft = response.data.draft;
        const draftWithoutItemId = resetUnwantedProperties(draft);
        return { ...draftWithoutItemId, draftReason: 'Template' };
    } catch {
        throw 'Could not load the draft from the template';
    }
};

const saveDraft = async (draft: ItemDraft) => {
    if (draft.draftReason === 'Template') {
        // Drafts for Templates cannot be saved
        throw new Error('Cannot save a Template as a draft');
    }

    const ensuredDraft = ensureApplicationId(draft);

    return await webApiClient.post<SaveDraftApiResponse, SaveDraftApiRequest>(
        '/selling/web/drafts',
        { draft: ensuredDraft }
    );
};

const resolveImages = (syiSlice: SyiState) => {
    const images = syiSlice.images.images
        .map((image) => {
            if (image.isListingImage) {
                return {
                    draftImage: null,
                    listingImage: {
                        imageId: image.imageId,
                        rotationDegrees: image.rotationDegrees,
                        filename: image.filename,
                        imageGroupId: image.imageGroupId,
                        isUploadedBySystem: image.isUploadedBySystem
                    },
                    imageUrl: image.imageUrl
                };
            } else {
                return {
                    draftImage: {
                        imageId: image.imageId,
                        rotationDegrees: image.rotationDegrees,
                        isUploadedBySystem: image.isUploadedBySystem
                    },
                    listingImage: null,
                    imageUrl: image.imageUrl
                };
            }
        })
        .filter(Boolean);
    return images;
};

export const buildDraftFromState = (syiSlice: SyiState) => {
    const draft: ItemDraftResponse = {
        itemFeaturesNotOfferedToMember: false,
        draftId: syiSlice.draft.draftId,
        draftReason: syiSlice.draft.draftReason,
        memberId: syiSlice.draft.memberId || 0,
        itemId: syiSlice.draft.itemId || 0,
        restart: syiSlice.draft.restart || false,
        publishedPromoted: syiSlice.draft.publishedPromoted || false,
        publishedHighlighted: syiSlice.draft.publishedHighlighted || false,
        createdDate: syiSlice.draft.createdDate || '',
        lastModifiedDate: syiSlice.draft.lastModifiedDate || '',
        reference: syiSlice.draft.reference,
        additionalDescriptions: syiSlice.draft.additionalDescriptions, //TODO
        metadata: syiSlice.draft.metadata,
        templateId: syiSlice.draft.templateId
            ? syiSlice.draft.templateId
            : undefined,
        templateName: syiSlice.draft.templateName
            ? syiSlice.draft.templateName
            : undefined,
        shortDescription: syiSlice.shortDescription.shortDescription,
        longDescription: syiSlice.longDescription.longDescription,
        descriptionLanguageCodeIso2:
            syiSlice.shortDescription.descriptionLanguageCodeIso2,
        currency: syiSlice.draft.currency,
        resellPartner: syiSlice.draft.resellPartner,
        categoryId: syiSlice.category.draftCategoryId,
        categoryPath: syiSlice.category.draftCategoryPath,
        images: resolveImages(syiSlice),
        openingBid: syiSlice.price.openingBid,
        reservePrice: syiSlice.price.reservePrice,
        fixedPrice: syiSlice.price.isPureBinSelected
            ? syiSlice.price.fixedPrice
            : syiSlice.price.auctionBinPrice,
        vat: syiSlice.price.vat,
        durationDays: syiSlice.itemDuration.durationDays,
        customEndTime: syiSlice.itemDuration.customEndTime,
        shippingOptions: syiSlice.shipping.selectedShippingOptions,
        selectedShippingSize: syiSlice.shipping.selectedShippingSize,
        fromCountry: syiSlice.shipping.fromCountry,
        itemCondition: syiSlice.itemCondition.itemCondition,
        allowedBuyerRegion: syiSlice.buyerRegion?.selectedBuyerRegion,
        campaignCode: syiSlice.campaignCode.campaignCode,
        campaignCodeId: syiSlice.campaignCode.campaignCodeId,
        displayPromoted: syiSlice.extraFeatures.displayPromoted,
        displayHighlighted: syiSlice.extraFeatures.displayHighlighted,
        charityId: syiSlice.charityCampaign.charityId,
        charity:
            featureToggles.isPennybridgeCharitiesEnabled &&
            syiSlice.charity?.charityId
                ? syiSlice.charity
                : undefined,
        allowZeroDayAuctions:
            syiSlice.charityCampaign.allowZeroDayAuctions || false,
        version: syiSlice.draft.version || 0,
        attributeValues: {
            termAttributeValues: syiSlice.attributes.categoryAttributes.map(
                (attribute) => {
                    const values = attribute.options.reduce((acc, option) => {
                        if (option.isActive && option.value) {
                            acc.push(option.value);
                        }
                        return acc;
                    }, [] as string[]);
                    return {
                        id: attribute.id,
                        backingField: attribute.backingField,
                        userGenerated: true,
                        values
                    };
                }
            )
        }
    };

    return draft;
};

export default {
    isPureBin,
    isAuctionBin,
    getDefaultTemplateValue,
    loadDefaultDraft,
    loadDefaultTemplateDraft,
    loadDraftFromTemplate,
    saveDraft
};
